import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import MuiPhoneNumber from 'mui-phone-number';
import React from 'react';

import { SelectInput } from '../../../../components/Forms/selectInput';
import { UnitType } from '../../../../types';

export interface AddMembershipDriverPayload {
  driverId?: string;
  driverEmail?: string;
  driverLicenseFile?: File;
  firstName?: string;
  lastName?: string;
  driverPhone?: string;
  licenseStateId?: string;
  unit?: {
    unitNumber: string;
    unitType: string;
    vehicleMake?: string;
    vehicleModel?: string;
    vehicleYear?: string;
    vinNumber: string;
  };
}

export interface ExistingDriverOption {
  driverId: string;
  driverName: string;
}

interface AddMembershipDriverModalProps {
  existingDrivers: ExistingDriverOption[];
  isLoadingExistingDrivers?: boolean;
  open: boolean;
  onClose: () => void;
  onSubmit: (payload: AddMembershipDriverPayload) => Promise<boolean>;
}

type DriverWizardStep =
  | 'choice'
  | 'existing'
  | 'existingDone'
  | 'newDriverInfo'
  | 'newDriverUnit'
  | 'newDriverUnitForm'
  | 'newDriverReview'
  | 'done';

const DRIVER_NAME_REGEX = /^[a-zA-Z ]+$/;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const UNIT_NUMBER_REGEX = /^\w+$/;
const VIN_REGEX = /^[0-9a-zA-Z]*$/;
const GVW_REGEX = /^[0-9]*$/;
const YEAR_REGEX = /^\d{4}$/;
const VEHICLE_MAKE_REGEX = /^[0-9a-zA-Z -]*$/;

const UNIT_TYPE_OPTIONS = [
  UnitType.Truck,
  UnitType.Tractor,
  UnitType.Bus,
  UnitType.Tanker,
  UnitType.Van,
  UnitType.Limousine,
  UnitType.PickUpTruck,
  UnitType.Other,
];

const AddMembershipDriverModal: React.FC<AddMembershipDriverModalProps> = ({
  existingDrivers,
  isLoadingExistingDrivers = false,
  open,
  onClose,
  onSubmit,
}) => {
  const [step, setStep] = React.useState<DriverWizardStep>('choice');
  const [existingDriver, setExistingDriver] = React.useState('');
  const [firstName, setFirstName] = React.useState('');
  const [lastName, setLastName] = React.useState('');
  const [driverEmail, setDriverEmail] = React.useState('');
  const [driverPhone, setDriverPhone] = React.useState('');
  const [driverLicenseType, setDriverLicenseType] = React.useState('');
  const [driverLicense, setDriverLicense] = React.useState('');
  const [driverLicenseFile, setDriverLicenseFile] = React.useState<File | null>(
    null,
  );
  const [unitNumber, setUnitNumber] = React.useState('');
  const [vinNumber, setVinNumber] = React.useState('');
  const [unitGvw, setUnitGvw] = React.useState('');
  const [unitYear, setUnitYear] = React.useState('');
  const [unitMake, setUnitMake] = React.useState('');
  const [unitModel, setUnitModel] = React.useState('');
  const [unitType, setUnitType] = React.useState('');
  const [shouldAddUnit, setShouldAddUnit] = React.useState<boolean | null>(
    null,
  );
  const [newDriverInfoErrors, setNewDriverInfoErrors] = React.useState<
    Record<string, string>
  >({});
  const [showNewDriverInfoErrors, setShowNewDriverInfoErrors] =
    React.useState(false);
  const [newUnitErrors, setNewUnitErrors] = React.useState<
    Record<string, string>
  >({});
  const [showNewUnitErrors, setShowNewUnitErrors] = React.useState(false);
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const selectedExistingDriver = React.useMemo(
    () => existingDrivers.find((driver) => driver.driverId === existingDriver),
    [existingDriver, existingDrivers],
  );

  const existingDriverOptions = React.useMemo(
    () =>
      existingDrivers.map((driver) => ({
        label: driver.driverName,
        value: driver.driverId,
      })),
    [existingDrivers],
  );

  const onDriverLicenseUpload = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0] || null;
    const fileName = file?.name ?? '';
    setDriverLicense(fileName);
    setDriverLicenseFile(file);
  };

  const validateNewDriverInfo = React.useCallback(() => {
    const errors: Record<string, string> = {};

    if (!firstName.trim()) {
      errors.firstName = 'Required';
    } else if (!DRIVER_NAME_REGEX.test(firstName.trim())) {
      errors.firstName = 'Enter a valid first name';
    }

    if (!lastName.trim()) {
      errors.lastName = 'Required';
    } else if (!DRIVER_NAME_REGEX.test(lastName.trim())) {
      errors.lastName = 'Enter a valid last name';
    }

    const email = driverEmail.trim();
    if (!email) {
      errors.driverEmail = 'Required';
    } else if (!EMAIL_REGEX.test(email)) {
      errors.driverEmail = 'Enter valid email';
    }

    const phoneRaw = driverPhone.trim();
    if (!phoneRaw) {
      errors.driverPhone = 'Required';
    }

    if (!driverLicense.trim()) {
      errors.driverLicense = 'Required';
    }

    setNewDriverInfoErrors(errors);
    return errors;
  }, [driverEmail, driverLicense, driverPhone, firstName, lastName]);

  const validateNewUnit = React.useCallback(() => {
    const errors: Record<string, string> = {};

    if (!unitNumber.trim()) {
      errors.unitNumber = 'Required';
    } else if (!UNIT_NUMBER_REGEX.test(unitNumber.trim())) {
      errors.unitNumber = 'Enter valid unit number';
    }

    const vin = vinNumber.trim();
    if (!vin) {
      errors.vinNumber = 'Required';
    } else if (vin.length < 8) {
      errors.vinNumber = 'Enter minimum 8 character';
    } else if (vin.length > 17) {
      errors.vinNumber = 'Enter maximum 17 character';
    } else if (!VIN_REGEX.test(vin)) {
      errors.vinNumber = 'Enter valid Vin';
    }

    if (!unitGvw.trim()) {
      errors.unitGvw = 'Required';
    } else if (!GVW_REGEX.test(unitGvw.trim())) {
      errors.unitGvw = 'Enter valid Gross vehicle weight';
    }

    if (!unitYear.trim()) {
      errors.unitYear = 'Required';
    } else if (!YEAR_REGEX.test(unitYear.trim())) {
      errors.unitYear = 'Enter a valid 4-digit year';
    }

    if (!unitMake.trim()) {
      errors.unitMake = 'Required';
    } else if (!VEHICLE_MAKE_REGEX.test(unitMake.trim())) {
      errors.unitMake = 'Enter valid vehicle make';
    }

    if (!unitType.trim()) {
      errors.unitType = 'Required';
    }

    setNewUnitErrors(errors);
    return errors;
  }, [unitGvw, unitMake, unitNumber, unitType, unitYear, vinNumber]);

  const resetAndClose = React.useCallback(() => {
    setStep('choice');
    setExistingDriver('');
    setFirstName('');
    setLastName('');
    setDriverEmail('');
    setDriverPhone('');
    setDriverLicenseType('');
    setDriverLicense('');
    setDriverLicenseFile(null);
    setUnitNumber('');
    setVinNumber('');
    setUnitGvw('');
    setUnitYear('');
    setUnitMake('');
    setUnitModel('');
    setUnitType('');
    setShouldAddUnit(null);
    setNewDriverInfoErrors({});
    setShowNewDriverInfoErrors(false);
    setNewUnitErrors({});
    setShowNewUnitErrors(false);
    setIsSubmitting(false);
    onClose();
  }, [onClose]);

  React.useEffect(() => {
    if (!open) {
      setStep('choice');
      setExistingDriver('');
      setFirstName('');
      setLastName('');
      setDriverEmail('');
      setDriverPhone('');
      setDriverLicenseType('');
      setDriverLicense('');
      setDriverLicenseFile(null);
      setUnitNumber('');
      setVinNumber('');
      setUnitGvw('');
      setUnitYear('');
      setUnitMake('');
      setUnitModel('');
      setUnitType('');
      setShouldAddUnit(null);
      setNewDriverInfoErrors({});
      setShowNewDriverInfoErrors(false);
      setNewUnitErrors({});
      setShowNewUnitErrors(false);
      setIsSubmitting(false);
    }
  }, [open]);

  React.useEffect(() => {
    if (showNewDriverInfoErrors) {
      validateNewDriverInfo();
    }
  }, [
    driverEmail,
    driverLicense,
    driverLicenseType,
    firstName,
    lastName,
    driverPhone,
    showNewDriverInfoErrors,
    validateNewDriverInfo,
  ]);

  React.useEffect(() => {
    if (showNewUnitErrors) {
      validateNewUnit();
    }
  }, [
    showNewUnitErrors,
    unitNumber,
    vinNumber,
    unitGvw,
    unitYear,
    unitMake,
    unitModel,
    unitType,
    validateNewUnit,
  ]);

  const submitExistingDriver = async () => {
    if (!existingDriver.trim()) {
      return;
    }

    const isSuccess = await onSubmit({
      driverId: selectedExistingDriver?.driverId || undefined,
    });

    if (isSuccess) {
      setStep('existingDone');
    }
  };

  const submitNewDriver = async () => {
    if (shouldAddUnit) {
      const errors = validateNewUnit();
      setShowNewUnitErrors(true);
      if (Object.keys(errors).length > 0) {
        return;
      }
    }

    setIsSubmitting(true);
    let isSuccess = false;
    try {
      isSuccess = await onSubmit({
        driverEmail: driverEmail.trim() || undefined,
        driverLicenseFile: driverLicenseFile || undefined,
        driverPhone: driverPhone.trim() || undefined,
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        licenseStateId: driverLicenseType.trim() || undefined,
        unit: shouldAddUnit
          ? {
              unitNumber: unitNumber.trim(),
              unitType: unitType.trim(),
              vehicleMake: unitMake.trim() || undefined,
              vehicleModel: unitModel.trim() || undefined,
              vehicleYear: unitYear.trim() || undefined,
              vinNumber: vinNumber.trim(),
            }
          : undefined,
      });
    } finally {
      setIsSubmitting(false);
    }

    if (isSuccess) {
      setStep('done');
    }
  };

  const headerCopy = (() => {
    if (step === 'choice') return { eyebrow: '', title: 'Add a Driver' };
    if (step === 'existing')
      return { eyebrow: 'Step 1 of 2', title: 'Select a Driver' };
    if (step === 'existingDone')
      return { eyebrow: 'Request Submitted', title: "You're All Set" };
    if (step === 'newDriverInfo')
      return { eyebrow: 'Step 1 of 3', title: 'Driver Information' };
    if (step === 'newDriverUnit')
      return { eyebrow: 'Step 2 of 3', title: 'Add a Driver' };
    if (step === 'newDriverUnitForm')
      return { eyebrow: 'Step 3 of 4', title: 'Unit Details' };
    if (step === 'newDriverReview')
      return {
        eyebrow: shouldAddUnit ? 'Step 4 of 4' : 'Step 3 of 3',
        title: 'Review & Confirm',
      };
    return { eyebrow: 'Request Submitted', title: "You're All Set" };
  })();

  const isNewDriverStepOneDisabled =
    !firstName.trim() ||
    !lastName.trim() ||
    !driverEmail.trim() ||
    !driverPhone.trim() ||
    !driverLicense.trim();
  const isNewDriverSubmitDisabled =
    !firstName.trim() || !lastName.trim() || !driverLicense.trim();

  return (
    <Dialog fullWidth maxWidth="sm" open={open} onClose={resetAndClose}>
      <DialogTitle sx={{ padding: '16px 22px 8px 22px' }}>
        <Typography
          sx={{
            color: '#C9A200',
            fontFamily: 'FiraSans-SemiBold',
            fontSize: '11px',
            letterSpacing: '.4px',
            minHeight: '14px',
            textTransform: 'uppercase',
          }}>
          {headerCopy.eyebrow}
        </Typography>
        <Typography
          sx={{
            color: '#1A1A1A',
            fontFamily: 'FiraSans-SemiBold',
            fontSize: '20px',
          }}>
          {headerCopy.title}
        </Typography>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ padding: '18px 22px' }}>
        {step === 'choice' ? (
          <Box sx={{ display: 'grid', gap: '12px' }}>
            <Typography
              sx={{
                color: '#2B2B2B',
                fontFamily: 'FiraSans-SemiBold',
                fontSize: '14px',
              }}>
              How would you like to add a driver?
            </Typography>

            <Box sx={{ display: 'flex', gap: '12px' }}>
              <Button
                onClick={() => setStep('existing')}
                sx={choiceButtonSx}
                variant="outlined">
                <Typography sx={choiceTitleSx}>
                  Assign Existing Driver
                </Typography>
                <Typography sx={choiceSubtitleSx}>
                  Use a driver already available in your DOT roster.
                </Typography>
              </Button>

              <Button
                onClick={() => setStep('newDriverInfo')}
                sx={choiceButtonSx}
                variant="outlined">
                <Typography sx={choiceTitleSx}>Add New Driver</Typography>
                <Typography sx={choiceSubtitleSx}>
                  Create a new driver request for Motor Club membership.
                </Typography>
              </Button>
            </Box>
          </Box>
        ) : null}

        {step === 'existing' ? (
          <Box sx={{ display: 'grid', gap: '14px' }}>
            <Typography sx={fieldLabelSx}>Existing Driver *</Typography>
            <SelectInput
              name="existingDriver"
              value={existingDriver}
              onChangeText={(value) => setExistingDriver(value)}
              optionsList={existingDriverOptions}
              size="small"
              placeHolder="Select a driver"
              defaultOptionLabel="Select a driver"
              isEditable={false}
            />

            {isLoadingExistingDrivers ? (
              <Typography sx={loadingHelperSx}>
                Loading available drivers...
              </Typography>
            ) : null}

            {!isLoadingExistingDrivers && !existingDrivers.length ? (
              <Typography sx={loadingHelperSx}>
                No eligible drivers found.
              </Typography>
            ) : null}
          </Box>
        ) : null}

        {step === 'newDriverInfo' ? (
          <Box sx={{ display: 'grid', gap: '14px' }}>
            <Box
              sx={{
                display: 'grid',
                gap: '12px',
                gridTemplateColumns: { sm: '1fr 1fr', xs: '1fr' },
              }}>
              <Box>
                <Typography sx={fieldLabelSx}>First Name *</Typography>
                <TextField
                  error={
                    showNewDriverInfoErrors && !!newDriverInfoErrors.firstName
                  }
                  helperText={
                    showNewDriverInfoErrors ? newDriverInfoErrors.firstName : ''
                  }
                  onChange={(event) => setFirstName(event.target.value)}
                  placeholder="Enter first name"
                  size="small"
                  sx={inputSx}
                  value={firstName}
                  fullWidth
                />
              </Box>

              <Box>
                <Typography sx={fieldLabelSx}>Last Name *</Typography>
                <TextField
                  error={
                    showNewDriverInfoErrors && !!newDriverInfoErrors.lastName
                  }
                  helperText={
                    showNewDriverInfoErrors ? newDriverInfoErrors.lastName : ''
                  }
                  onChange={(event) => setLastName(event.target.value)}
                  placeholder="Enter last name"
                  size="small"
                  sx={inputSx}
                  value={lastName}
                  fullWidth
                />
              </Box>
            </Box>

            <Box
              sx={{
                display: 'grid',
                gap: '12px',
                gridTemplateColumns: { sm: '1fr 1fr', xs: '1fr' },
              }}>
              <Box>
                <Typography sx={fieldLabelSx}>Email Address *</Typography>
                <TextField
                  onChange={(event) => setDriverEmail(event.target.value)}
                  placeholder="name@email.com"
                  size="small"
                  sx={inputSx}
                  value={driverEmail}
                  error={
                    showNewDriverInfoErrors && !!newDriverInfoErrors.driverEmail
                  }
                  helperText={
                    showNewDriverInfoErrors
                      ? newDriverInfoErrors.driverEmail
                      : ''
                  }
                  fullWidth
                />
              </Box>

              <Box>
                <Typography sx={fieldLabelSx}>Phone Number *</Typography>
                <MuiPhoneNumber
                  autoFormat={true}
                  countryCodeEditable={false}
                  defaultCountry="us"
                  disableAreaCodes={true}
                  disableCountryCode={false}
                  inputProps={{
                    maxLength: driverPhone.startsWith('+52') ? 13 : undefined,
                  }}
                  onChange={(
                    value:
                      | string
                      | React.ChangeEvent<
                          HTMLInputElement | HTMLTextAreaElement
                        >,
                  ) =>
                    setDriverPhone(
                      typeof value === 'string' ? value : value.target.value,
                    )
                  }
                  onlyCountries={['us', 'mx', 'ca']}
                  sx={phoneInputSx}
                  value={driverPhone || undefined}
                  fullWidth
                />
                {showNewDriverInfoErrors && newDriverInfoErrors.driverPhone ? (
                  <Typography sx={fieldErrorSx}>
                    {newDriverInfoErrors.driverPhone}
                  </Typography>
                ) : null}
              </Box>
            </Box>

            <Box>
              <Typography sx={fieldLabelSx}>
                Upload Driver&apos;s License *
              </Typography>
              <Box sx={uploadFieldSx}>
                <Typography sx={uploadNameSx}>
                  {driverLicense || 'No file chosen'}
                </Typography>
                <Button
                  component="label"
                  sx={uploadButtonSx}
                  variant="outlined">
                  Choose File
                  <input
                    accept=".pdf,.png,.jpg,.jpeg"
                    hidden
                    onChange={onDriverLicenseUpload}
                    type="file"
                  />
                </Button>
              </Box>
              {showNewDriverInfoErrors && newDriverInfoErrors.driverLicense ? (
                <Typography sx={fieldErrorSx}>
                  {newDriverInfoErrors.driverLicense}
                </Typography>
              ) : null}
            </Box>
          </Box>
        ) : null}

        {step === 'newDriverUnit' ? (
          <Box sx={{ display: 'grid', gap: '12px' }}>
            <Box sx={proTipCardSx}>
              <Box sx={proTipIconSx}>{'💡'}</Box>
              <Typography sx={proTipTextSx}>
                <b>Pro tip:</b> Registering a unit for this driver now enrolls
                it under Roadside Assistance at <b>no additional charge</b>.
              </Typography>
            </Box>

            <Typography
              sx={{
                color: '#2B2B2B',
                fontFamily: 'FiraSans-SemiBold',
                fontSize: '14px',
              }}>
              Do you want to register a new unit for this driver?
            </Typography>

            <Box
              sx={{
                display: 'flex',
                gap: '10px',
              }}>
              <Button
                onClick={() => {
                  setShouldAddUnit(true);
                  setStep('newDriverUnitForm');
                }}
                sx={{
                  ...choiceButtonSx,
                  ...(shouldAddUnit === true ? selectedUnitOptionButtonSx : {}),
                }}
                variant="outlined">
                <Typography
                  sx={{
                    ...choiceTitleSx,
                    color: 'var(--green, #2F8F4E)',
                  }}>
                  ✓ Yes, register a unit
                </Typography>
                <Typography sx={choiceSubtitleSx}>
                  Add VIN and vehicle details now
                </Typography>
              </Button>

              <Button
                onClick={() => {
                  setShouldAddUnit(false);
                  setStep('newDriverReview');
                }}
                sx={{
                  ...choiceButtonSx,
                  ...(shouldAddUnit === false
                    ? selectedUnitOptionButtonSx
                    : {}),
                }}
                variant="outlined">
                <Typography sx={choiceTitleSx}>Not right now</Typography>
                <Typography sx={choiceSubtitleSx}>
                  You can add a unit later
                </Typography>
              </Button>
            </Box>
          </Box>
        ) : null}

        {step === 'newDriverUnitForm' ? (
          <Box sx={{ display: 'grid', gap: '14px' }}>
            <Box>
              <Typography sx={fieldLabelSx}>Unit Number *</Typography>
              <TextField
                onChange={(event) => setUnitNumber(event.target.value)}
                placeholder="e.g. PU-102"
                size="small"
                sx={inputSx}
                value={unitNumber}
                error={showNewUnitErrors && !!newUnitErrors.unitNumber}
                helperText={showNewUnitErrors ? newUnitErrors.unitNumber : ''}
                fullWidth
              />
            </Box>

            <Box>
              <Typography sx={fieldLabelSx}>VIN *</Typography>
              <TextField
                onChange={(event) => setVinNumber(event.target.value)}
                placeholder="17-character VIN"
                size="small"
                sx={inputSx}
                value={vinNumber}
                error={showNewUnitErrors && !!newUnitErrors.vinNumber}
                helperText={showNewUnitErrors ? newUnitErrors.vinNumber : ''}
                fullWidth
              />
            </Box>

            <Box
              sx={{
                display: 'grid',
                gap: '12px',
                gridTemplateColumns: { sm: '1fr 1fr', xs: '1fr' },
              }}>
              <Box>
                <Typography sx={fieldLabelSx}>
                  Gross Vehicle Weight (GVW) *
                </Typography>
                <TextField
                  onChange={(event) => setUnitGvw(event.target.value)}
                  placeholder="e.g. 33,000 lbs"
                  size="small"
                  sx={inputSx}
                  value={unitGvw}
                  error={showNewUnitErrors && !!newUnitErrors.unitGvw}
                  helperText={showNewUnitErrors ? newUnitErrors.unitGvw : ''}
                  fullWidth
                />
              </Box>

              <Box>
                <Typography sx={fieldLabelSx}>Year *</Typography>
                <TextField
                  inputProps={{ maxLength: 4 }}
                  onChange={(event) => setUnitYear(event.target.value)}
                  placeholder="e.g. 2022"
                  size="small"
                  sx={inputSx}
                  value={unitYear}
                  error={showNewUnitErrors && !!newUnitErrors.unitYear}
                  helperText={showNewUnitErrors ? newUnitErrors.unitYear : ''}
                  fullWidth
                />
              </Box>
            </Box>

            <Box
              sx={{
                display: 'grid',
                gap: '12px',
                gridTemplateColumns: { sm: '1fr 1fr', xs: '1fr' },
              }}>
              <Box>
                <Typography sx={fieldLabelSx}>Make *</Typography>
                <TextField
                  onChange={(event) => setUnitMake(event.target.value)}
                  placeholder="e.g. Freightliner"
                  size="small"
                  sx={inputSx}
                  value={unitMake}
                  error={showNewUnitErrors && !!newUnitErrors.unitMake}
                  helperText={showNewUnitErrors ? newUnitErrors.unitMake : ''}
                  fullWidth
                />
              </Box>

              <Box>
                <Typography sx={fieldLabelSx}>Model</Typography>
                <TextField
                  onChange={(event) => setUnitModel(event.target.value)}
                  placeholder="e.g. Cascadia"
                  size="small"
                  sx={inputSx}
                  value={unitModel}
                  error={showNewUnitErrors && !!newUnitErrors.unitModel}
                  helperText={showNewUnitErrors ? newUnitErrors.unitModel : ''}
                  fullWidth
                />
              </Box>
            </Box>

            <Box>
              <Typography sx={fieldLabelSx}>Unit Type *</Typography>
              <SelectInput
                name="unitType"
                value={unitType}
                onChangeText={(value) => setUnitType(value)}
                optionsList={UNIT_TYPE_OPTIONS.map((typeOption) => ({
                  label: typeOption,
                  value: typeOption,
                }))}
                size="small"
                placeHolder="Select a type..."
                defaultOptionLabel="Select a type..."
                sortByOptionDisable={true}
                errors={showNewUnitErrors ? newUnitErrors.unitType || '' : ''}
                isEditable={false}
              />
            </Box>
          </Box>
        ) : null}

        {step === 'newDriverReview' ? (
          <Box
            sx={{
              backgroundColor: '#FBEED3',
              border: '1px solid #E8D39C',
              borderRadius: '10px',
              color: '#5C4004',
              fontFamily: 'FiraSans-Regular',
              fontSize: '13px',
              lineHeight: 1.6,
              padding: '14px 16px',
            }}>
            By submitting this request, you acknowledge that{' '}
            <b>{`${firstName} ${lastName}`.trim() || 'this driver'}</b> will not
            be immediately enrolled. Our team will review your request and
            contact you shortly to confirm membership eligibility and collect
            payment, if necessary.
            <br />
          </Box>
        ) : null}

        {step === 'existingDone' || step === 'done' ? (
          <Box sx={{ padding: '12px 0 6px', textAlign: 'center' }}>
            <Typography
              sx={{
                color: '#2F8F4E',
                fontFamily: 'FiraSans-SemiBold',
                fontSize: '34px',
                lineHeight: 1,
                marginBottom: '8px',
              }}>
              ✓
            </Typography>
            <Typography
              sx={{
                color: '#1A1A1A',
                fontFamily: 'FiraSans-SemiBold',
                fontSize: '16px',
                marginBottom: '6px',
              }}>
              Your request has been submitted.
            </Typography>
            <Typography
              sx={{
                color: '#66666F',
                fontFamily: 'FiraSans-Regular',
                fontSize: '13px',
                lineHeight: 1.55,
              }}>
              Our team will contact you within one business day.
            </Typography>
            <Typography
              sx={{
                color: '#66666F',
                fontFamily: 'FiraSans-Regular',
                fontSize: '13px',
                lineHeight: 1.55,
              }}>
              Thank you for being a valued Motor Club member!
            </Typography>
          </Box>
        ) : null}
      </DialogContent>

      <Divider />

      <DialogActions sx={{ backgroundColor: '#F7F7F8', padding: '14px 22px' }}>
        {step === 'choice' ? (
          <Button onClick={resetAndClose} sx={cancelButtonSx}>
            Cancel
          </Button>
        ) : null}

        {step === 'existing' ? (
          <>
            <Button onClick={() => setStep('choice')} sx={backButtonSx}>
              {'<-'} Back
            </Button>
            <Box sx={{ flex: 1 }} />
            <Button onClick={resetAndClose} sx={cancelButtonSx}>
              Cancel
            </Button>
            <Button
              disabled={
                !existingDriver.trim() ||
                !existingDrivers.length ||
                isLoadingExistingDrivers
              }
              onClick={submitExistingDriver}
              sx={primaryButtonSx}
              variant="contained">
              Next
            </Button>
          </>
        ) : null}

        {step === 'newDriverInfo' ? (
          <>
            <Button onClick={() => setStep('choice')} sx={backButtonSx}>
              {'<-'} Back
            </Button>
            <Box sx={{ flex: 1 }} />
            <Button onClick={resetAndClose} sx={cancelButtonSx}>
              Cancel
            </Button>
            <Button
              disabled={isNewDriverStepOneDisabled}
              onClick={() => {
                setShowNewDriverInfoErrors(true);
                const errors = validateNewDriverInfo();
                if (Object.keys(errors).length === 0) {
                  setStep('newDriverUnit');
                }
              }}
              sx={primaryButtonSx}
              variant="contained">
              Next
            </Button>
          </>
        ) : null}

        {step === 'newDriverUnit' ? (
          <>
            <Button onClick={() => setStep('newDriverInfo')} sx={backButtonSx}>
              {'<-'} Back
            </Button>
            <Box sx={{ flex: 1 }} />
            <Button onClick={resetAndClose} sx={cancelButtonSx}>
              Cancel
            </Button>
          </>
        ) : null}

        {step === 'newDriverReview' ? (
          <>
            <Button
              onClick={() =>
                setStep(shouldAddUnit ? 'newDriverUnitForm' : 'newDriverUnit')
              }
              sx={backButtonSx}>
              {'<-'} Back
            </Button>
            <Box sx={{ flex: 1 }} />
            <Button onClick={resetAndClose} sx={cancelButtonSx}>
              Cancel Request
            </Button>
            <Button
              disabled={isNewDriverSubmitDisabled || isSubmitting}
              onClick={submitNewDriver}
              sx={primaryButtonSx}
              variant="contained">
              {isSubmitting ? (
                <CircularProgress size={18} sx={{ color: '#241A2E' }} />
              ) : (
                'Agree'
              )}
            </Button>
          </>
        ) : null}

        {step === 'existingDone' || step === 'done' ? (
          <Button
            onClick={resetAndClose}
            sx={{ ...primaryButtonSx, width: '100%' }}
            variant="contained">
            Back to Dashboard
          </Button>
        ) : null}

        {step === 'newDriverUnitForm' ? (
          <>
            <Button onClick={() => setStep('newDriverUnit')} sx={backButtonSx}>
              {'<-'} Back
            </Button>
            <Box sx={{ flex: 1 }} />
            <Button onClick={resetAndClose} sx={cancelButtonSx}>
              Cancel
            </Button>
            <Button
              onClick={() => {
                setShowNewUnitErrors(true);
                const errors = validateNewUnit();
                if (Object.keys(errors).length === 0) {
                  setStep('newDriverReview');
                }
              }}
              sx={primaryButtonSx}
              variant="contained">
              Next
            </Button>
          </>
        ) : null}
      </DialogActions>
    </Dialog>
  );
};

const choiceButtonSx = {
  '&:hover': {
    backgroundColor: '#F7F7F8',
    borderColor: '#DEC330',
  },
  alignItems: 'flex-start',
  borderColor: '#E2E2E6',
  borderRadius: '10px',
  color: '#2B2B2B',
  display: 'flex',
  flex: 1,
  flexDirection: 'column',
  gap: '4px',
  justifyContent: 'flex-start',
  minHeight: '84px',
  padding: '14px 16px',
  textTransform: 'none',
};

const choiceTitleSx = {
  color: '#1A1A1A',
  fontFamily: 'FiraSans-SemiBold',
  fontSize: '14px',
};

const fieldLabelSx = {
  color: '#1A1A1A',
  fontFamily: 'FiraSans-SemiBold',
  fontSize: '12px',
  marginBottom: '6px',
};

const inputSx = {
  '& .MuiOutlinedInput-root': {
    backgroundColor: '#FFFFFF',
    borderRadius: '8px',
  },
};

const phoneInputSx = {
  '& .MuiOutlinedInput-root': {
    backgroundColor: '#FFFFFF',
    borderRadius: '8px',
  },
  width: '100%',
};

const loadingHelperSx = {
  color: '#66666F',
  fontFamily: 'FiraSans-Regular',
  fontSize: '11.5px',
};

const fieldErrorSx = {
  color: '#d32f2f',
  fontFamily: 'FiraSans-Regular',
  fontSize: '12px',
  marginTop: '6px',
};

const uploadFieldSx = {
  alignItems: 'center',
  backgroundColor: '#FFFFFF',
  border: '1px solid #D8D8DD',
  borderRadius: '8px',
  display: 'flex',
  gap: '10px',
  justifyContent: 'space-between',
  minHeight: '42px',
  padding: '8px 10px',
};

const uploadNameSx = {
  color: '#66666F',
  fontFamily: 'FiraSans-Regular',
  fontSize: '12px',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const uploadButtonSx = {
  '&:hover': {
    backgroundColor: '#F8F0C3',
    borderColor: '#DEC330',
  },
  borderColor: '#DEC330',
  color: '#241A2E',
  fontFamily: 'FiraSans-SemiBold',
  minWidth: '92px',
  textTransform: 'none',
};

const selectedUnitOptionButtonSx = {
  backgroundColor: '#FFF8D8',
  borderColor: '#DEC330',
};

const proTipCardSx = {
  alignItems: 'flex-start',
  backgroundColor: '#FBEED3',
  border: '1px solid #E8D39C',
  borderRadius: '10px',
  display: 'flex',
  gap: '10px',
  padding: '12px 14px',
};

const proTipIconSx = {
  fontSize: '16px',
  lineHeight: 1,
  marginTop: '1px',
};

const proTipTextSx = {
  color: '#5C4004',
  fontFamily: 'FiraSans-Regular',
  fontSize: '12.5px',
  lineHeight: 1.5,
};

const choiceSubtitleSx = {
  color: '#66666F',
  fontFamily: 'FiraSans-Regular',
  fontSize: '12px',
  textAlign: 'left',
};

const cancelButtonSx = {
  color: '#66666F',
  fontFamily: 'FiraSans-SemiBold',
  textTransform: 'none',
};

const backButtonSx = {
  color: '#66666F',
  fontFamily: 'FiraSans-SemiBold',
  fontSize: '12px',
  minWidth: 'fit-content',
  textTransform: 'none',
};

const primaryButtonSx = {
  '&:hover': {
    backgroundColor: '#CBB12B',
  },
  backgroundColor: '#DEC330',
  borderRadius: '8px',
  color: '#241A2E',
  fontFamily: 'FiraSans-SemiBold',
  minWidth: '110px',
  textTransform: 'none',
};

export default AddMembershipDriverModal;
